import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import PageHero from "@/components/PageHero";
import newsHero from "@/assets/news-hero.jpg";
import { activeArticles, blogCategories, BlogCategory } from "@/data/blogArticles";
import { Briefcase, ArrowRight, Search, X, SortAsc, SortDesc } from "lucide-react";
import { Input } from "@/components/ui/input";

const allAuthors = [...new Set(activeArticles.map((a) => a.author))].sort();

const NewsPage = () => {
  const [activeCategory, setActiveCategory] = useState<BlogCategory | "All">("All");
  const [search, setSearch] = useState("");
  const [selectedAuthor, setSelectedAuthor] = useState("");
  const [sortOrder, setSortOrder] = useState<"newest" | "oldest">("newest");

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    let results = activeArticles.filter((a) => {
      if (activeCategory !== "All" && a.category !== activeCategory) return false;
      if (selectedAuthor && a.author !== selectedAuthor) return false;
      if (q) {
        const searchable = `${a.title} ${a.excerpt} ${a.author} ${a.category} ${a.date}`.toLowerCase();
        return searchable.includes(q);
      }
      return true;
    });

    results.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });

    return results;
  }, [search, activeCategory, selectedAuthor, sortOrder]);

  const hasFilters = search || selectedAuthor || activeCategory !== "All";

  const clearAll = () => {
    setSearch("");
    setSelectedAuthor("");
    setActiveCategory("All");
  };

  return (
    <Layout>
      <SEO title="News & Insights | Vaughan McLean" description="Stay informed with the latest legal news, case results, and industry insights from the attorneys at Vaughan McLean." />
      <PageHero backgroundImage={newsHero} title="Blog" subtitle="News & Insights" />

      <section className="section-dark py-24">
        <div className="container mx-auto px-8 lg:px-16">
          {/* Search & Filter Bar */}
          <div className="mb-12 space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Keyword search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by keyword, title, or attorney..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 bg-card border-border text-foreground placeholder:text-muted-foreground"
                />
              </div>

              {/* Author filter */}
              <select
                value={selectedAuthor}
                onChange={(e) => setSelectedAuthor(e.target.value)}
                className="h-10 rounded-md border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:w-64"
              >
                <option value="">All Authors</option>
                {allAuthors.map((author) => (
                  <option key={author} value={author}>
                    {author}
                  </option>
                ))}
              </select>

              {/* Sort by date */}
              <button
                onClick={() => setSortOrder(sortOrder === "newest" ? "oldest" : "newest")}
                className="h-10 rounded-md border border-border bg-card px-4 text-sm text-foreground hover:bg-accent/50 transition-colors flex items-center gap-2 shrink-0"
              >
                {sortOrder === "newest" ? (
                  <SortDesc className="w-4 h-4" />
                ) : (
                  <SortAsc className="w-4 h-4" />
                )}
                {sortOrder === "newest" ? "Newest First" : "Oldest First"}
              </button>
            </div>

            {hasFilters && (
              <button
                onClick={clearAll}
                className="inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase font-body text-muted-foreground hover:text-primary transition-colors"
              >
                <X className="w-3 h-3" /> Clear all filters
              </button>
            )}
          </div>

          <div className="flex flex-col lg:flex-row gap-16">
            {/* Articles */}
            <div className="flex-1">
              <p className="text-xs text-muted-foreground font-body mb-6">
                {filtered.length} {filtered.length === 1 ? "article" : "articles"} found
              </p>
              <div className="space-y-0">
                {filtered.map((article, i) => (
                  <motion.article
                    key={article.slug}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.05 }}
                    viewport={{ once: true }}
                  >
                    <Link
                      to={`/news/${article.slug}`}
                      className="block border-b border-border py-10 group cursor-pointer hover:bg-card/50 transition-colors px-4 -mx-4"
                    >
                      <p className="text-xs font-body text-muted-foreground mb-1">{article.date}</p>
                      <span className="inline-block text-[10px] tracking-[0.2em] uppercase font-body text-primary mb-3">
                        {article.category}
                      </span>
                      <h3 className="font-heading text-2xl text-foreground group-hover:text-primary transition-colors mb-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-muted-foreground font-body mb-2 italic">
                        {article.author}
                      </p>
                      <p className="text-muted-foreground font-body text-sm leading-relaxed mb-3">
                        {article.excerpt}
                      </p>
                      <span className="inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase font-body text-muted-foreground group-hover:text-primary transition-colors">
                        Read More <ArrowRight className="w-3 h-3" />
                      </span>
                    </Link>
                  </motion.article>
                ))}
              </div>
              {filtered.length === 0 && (
                <p className="text-muted-foreground font-body text-center py-16">
                  No articles found matching your criteria.
                </p>
              )}
            </div>

            {/* Sidebar */}
            <aside className="lg:w-72 shrink-0">
              <div className="lg:sticky lg:top-40">
                <h2 className="text-xs tracking-[0.2em] uppercase font-body text-primary mb-6">
                  Categories
                </h2>
                <div className="space-y-1">
                  <button
                    onClick={() => setActiveCategory("All")}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-body transition-colors border-b border-border ${
                      activeCategory === "All"
                        ? "text-primary"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <Briefcase className="w-4 h-4" />
                    All Posts
                    <span className="ml-auto text-xs text-muted-foreground">
                      {activeArticles.length}
                    </span>
                  </button>
                  {blogCategories.map((cat) => {
                    const count = activeArticles.filter((a) => a.category === cat).length;
                    return (
                      <button
                        key={cat}
                        onClick={() => setActiveCategory(cat)}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-body transition-colors border-b border-border ${
                          activeCategory === cat
                            ? "text-primary"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        <Briefcase className="w-4 h-4" />
                        {cat}
                        <span className="ml-auto text-xs text-muted-foreground">{count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default NewsPage;
